import { supabase } from "@supabaseClient/supabaseClient";
import { fetchAllPages, PageResponse } from "@utils/fetchAllPages";

/**
 * Product data structure from database relations
 */
interface ProductData {
  name: string;
  sku: string;
}

/**
 * Inventory record with product relation
 */
interface InventoryRecord {
  id: string;
  quantity: number;
  committed_quantity?: number;
  product_id: string;
  products: ProductData;
}

/**
 * Supabase error type
 */
interface SupabaseError {
  message: string;
  details?: string;
  hint?: string;
  code?: string;
}

export interface IStockCommitment {
  id: string;
  order_id: string;
  order_item_id: string;
  inventory_id?: string; // Made optional for backorders
  product_id?: string; // Added for direct product reference
  quantity_committed: number;
  quantity_fulfilled: number;
  status: "active" | "fulfilled" | "cancelled" | "released";
  organization_id: string; // Added for multi-tenant support
  committed_at: string;
  fulfilled_at?: string;
  released_at?: string;
}

export interface ICreateStockCommitment {
  order_id: string;
  order_item_id: string;
  inventory_id?: string; // Made optional for backorders
  product_id?: string; // Added for direct product reference
  quantity_committed: number;
  organization_id: string; // Added for multi-tenant support
}

export interface IUpdateStockCommitment {
  id: string;
  quantity_committed?: number;
  quantity_fulfilled?: number;
  status?: "active" | "fulfilled" | "cancelled" | "released";
}

export interface IStockAvailability {
  inventory_id: string;
  product_id: string;
  total_quantity: number;
  available_quantity: number;
  committed_quantity: number;
  product_name: string;
  product_sku: string;
}

export class StockCommitmentsClient {
  /**
   * Create a new stock commitment
   */
  static async createStockCommitment(
    commitment: ICreateStockCommitment
  ): Promise<IStockCommitment> {
    console.log("Creating stock commitment:", commitment);

    const { data, error } = await supabase
      .from("stock_commitments")
      .insert(commitment)
      .select()
      .single();

    if (error) {
      console.error("Error creating stock commitment:", error);
      throw error;
    }

    console.log("Stock commitment created successfully:", data);
    return data;
  }

  /**
   * Get all stock commitments for an order
   */
  static async getStockCommitmentsByOrderId(
    orderId: string
  ): Promise<IStockCommitment[]> {
    const { data, error } = await supabase
      .from("stock_commitments")
      .select("*")
      .eq("order_id", orderId);

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Get all stock commitments for an order item
   */
  static async getStockCommitmentsByOrderItemId(
    orderItemId: string
  ): Promise<IStockCommitment[]> {
    const { data, error } = await supabase
      .from("stock_commitments")
      .select("*")
      .eq("order_item_id", orderItemId);

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Get all stock commitments for an inventory item
   */
  static async getStockCommitmentsByInventoryId(
    inventoryId: string
  ): Promise<IStockCommitment[]> {
    const { data, error } = await supabase
      .from("stock_commitments")
      .select("*")
      .eq("inventory_id", inventoryId);

    if (error) {
      throw error;
    }

    return data || [];
  }

  /**
   * Update a stock commitment
   */
  static async updateStockCommitment(
    commitment: IUpdateStockCommitment
  ): Promise<IStockCommitment> {
    const { id, ...updateData } = commitment;

    const { data, error } = await supabase
      .from("stock_commitments")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  /**
   * Delete a stock commitment
   */
  static async deleteStockCommitment(id: string): Promise<void> {
    const { error } = await supabase
      .from("stock_commitments")
      .delete()
      .eq("id", id);

    if (error) {
      throw error;
    }
  }

  /**
   * Delete all stock commitments for an order
   */
  static async deleteStockCommitmentsByOrderId(orderId: string): Promise<void> {
    const { error } = await supabase
      .from("stock_commitments")
      .delete()
      .eq("order_id", orderId);

    if (error) {
      throw error;
    }
  }

  /**
   * Delete all stock commitments for an order item
   */
  static async deleteStockCommitmentsByOrderItemId(
    orderItemId: string
  ): Promise<void> {
    const { error } = await supabase
      .from("stock_commitments")
      .delete()
      .eq("order_item_id", orderItemId);

    if (error) {
      throw error;
    }
  }

  /**
   * Get stock availability for a specific inventory item
   */
  static async getStockAvailability(
    productId: string
  ): Promise<IStockAvailability> {
    console.log("Getting stock availability for product ID:", productId);

    // Get all inventory records for this product and sum them up. Paginated so
    // a product with many inventory rows is not truncated by the 1000-row cap.
    const data = await fetchAllPages<InventoryRecord>((from, to) =>
      supabase
        .from("inventory")
        .select(
          `
        id,
        product_id,
        quantity,
        committed_quantity,
        products!inventory_product_id_fkey (
          name,
          sku
        )
      `
        )
        .eq("product_id", productId)
        .order("id", { ascending: true })
        .range(from, to) as unknown as PromiseLike<PageResponse<InventoryRecord>>
    );

    console.log("Stock availability data:", data);

    if (data.length === 0) {
      return {
        inventory_id: "",
        product_id: productId,
        total_quantity: 0,
        available_quantity: 0,
        committed_quantity: 0,
        product_name: "",
        product_sku: "",
      };
    }

    // Sum up all quantities across all inventory records for this product
    const total_quantity = data.reduce((sum, item) => sum + item.quantity, 0);
    const total_committed = data.reduce(
      (sum, item) => sum + (item.committed_quantity || 0),
      0
    );
    const available_quantity = total_quantity - total_committed;

    // Use the first inventory record for product info (they should all be the same product)
    const firstRecord = data[0];

    return {
      inventory_id: firstRecord.id, // Return the first inventory ID for reference
      product_id: firstRecord.product_id,
      total_quantity: total_quantity,
      available_quantity: Math.max(0, available_quantity),
      committed_quantity: total_committed,
      product_name: firstRecord.products.name,
      product_sku: firstRecord.products.sku,
    };
  }

  /**
   * Get stock availability for multiple inventory items
   */
  static async getBulkStockAvailability(
    productIds: string[]
  ): Promise<IStockAvailability[]> {
    if (productIds.length === 0) return [];

    const chunkSize = 500;
    const chunks: string[][] = [];
    for (let i = 0; i < productIds.length; i += chunkSize) {
      chunks.push(productIds.slice(i, i + chunkSize));
    }

    // Group by product_id and sum quantities
    const productMap = new Map<string, IStockAvailability>();

    for (const chunk of chunks) {
      // Chunk caps the product_id IN-list; pagination caps the result rows, so
      // products with many inventory lines are not truncated by the 1000 cap.
      const data = await fetchAllPages<InventoryRecord>((from, to) =>
        supabase
          .from("inventory")
          .select(
            `
          id,
          product_id,
          quantity,
          committed_quantity,
          products!inventory_product_id_fkey (
            name,
            sku
          )
        `
          )
          .in("product_id", chunk)
          .order("id", { ascending: true })
          .range(from, to) as unknown as PromiseLike<PageResponse<InventoryRecord>>
      );

      data.forEach((item) => {
        const productId = item.product_id;
        const existing = productMap.get(productId);

        if (existing) {
          // Add to existing totals
          existing.total_quantity += item.quantity;
          existing.committed_quantity += item.committed_quantity || 0;
          existing.available_quantity =
            existing.total_quantity - existing.committed_quantity;
        } else {
          // Create new entry
          const available_quantity =
            item.quantity - (item.committed_quantity || 0);
          productMap.set(productId, {
            inventory_id: item.id,
            product_id: item.product_id,
            total_quantity: item.quantity,
            available_quantity: Math.max(0, available_quantity),
            committed_quantity: item.committed_quantity || 0,
            product_name: item.products.name,
            product_sku: item.products.sku,
          });
        }
      });
    }

    return Array.from(productMap.values());
  }

  /**
   * Check if there's enough stock available for a commitment
   */
  static async checkStockAvailability(
    productId: string,
    requestedQuantity: number
  ): Promise<{ available: boolean; availableQuantity: number; shortfall: number }> {
    const availability = await this.getStockAvailability(productId);

    const available = availability.available_quantity >= requestedQuantity;
    const shortfall = Math.max(0, requestedQuantity - availability.available_quantity);

    return {
      available,
      availableQuantity: availability.available_quantity,
      shortfall,
    };
  }

  /**
   * Commit stock for an order item
   */
  static async commitStock(
    orderId: string,
    orderItemId: string,
    productId: string,
    quantity: number,
    organizationId: string
  ): Promise<IStockCommitment> {
    console.log("Committing stock:", { orderId, orderItemId, productId, quantity });

    try {
      // Check stock availability (but don't throw error - allow overselling)
      const availability = await this.checkStockAvailability(productId, quantity);
      console.log("Stock availability check:", availability);

      // Log warning if insufficient stock, but continue with commitment
      if (!availability.available) {
        console.warn(
          `Insufficient stock for commitment. Available: ${availability.availableQuantity}, Requested: ${quantity}, Shortfall: ${availability.shortfall}`
        );
      }

      // Get all inventory records for this product to find one to commit from
      const { data: inventoryRecords, error: inventoryError } = await supabase
        .from("inventory")
        .select("id, quantity, committed_quantity")
        .eq("product_id", productId)
        .order("quantity", { ascending: false }); // Start with records that have the most stock

      if (inventoryError) {
        throw inventoryError;
      }

      let commitment: IStockCommitment;

      if (!inventoryRecords || inventoryRecords.length === 0) {
        // No inventory records - create backorder commitment (no inventory_id)
        console.log("No inventory records found, creating backorder commitment");
        commitment = await this.createStockCommitment({
          order_id: orderId,
          order_item_id: orderItemId,
          product_id: productId,
          quantity_committed: quantity,
          organization_id: organizationId,
        });
      } else {
        // Use the first inventory record (the one with most stock)
        const selectedInventoryRecord = inventoryRecords[0];

        // Create the commitment with inventory_id
        commitment = await this.createStockCommitment({
          order_id: orderId,
          order_item_id: orderItemId,
          inventory_id: selectedInventoryRecord.id,
          product_id: productId,
          quantity_committed: quantity,
          organization_id: organizationId,
        });

        // Update the inventory committed_quantity
        await this.updateInventoryCommittedQuantity(
          selectedInventoryRecord.id,
          quantity,
          "add"
        );
      }

      return commitment;
    } catch (error) {
      console.error("Error in commitStock:", error);
      throw error;
    }
  }

  /**
   * Release stock commitment
   */
  static async releaseStockCommitment(commitmentId: string): Promise<void> {
    // Get the commitment details
    const { data: commitment, error: fetchError } = await supabase
      .from("stock_commitments")
      .select("*")
      .eq("id", commitmentId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    // Update the commitment status
    await this.updateStockCommitment({
      id: commitmentId,
      status: "released",
    });

    // Update the inventory committed_quantity
    await this.updateInventoryCommittedQuantity(
      commitment.inventory_id,
      commitment.quantity_committed,
      "subtract"
    );
  }

  /**
   * Release all stock commitments for an order
   */
  static async releaseAllStockCommitmentsForOrder(
    orderId: string
  ): Promise<void> {
    // Get all commitments for the order
    const commitments = await this.getStockCommitmentsByOrderId(orderId);

    // Release each commitment
    for (const commitment of commitments) {
      if (commitment.status === "active") {
        await this.releaseStockCommitment(commitment.id);
      }
    }
  }

  /**
   * Update inventory committed quantity
   */
  private static async updateInventoryCommittedQuantity(
    inventoryId: string,
    quantity: number,
    operation: "add" | "subtract"
  ): Promise<void> {
    console.log("Updating inventory committed quantity:", {
      inventoryId,
      quantity,
      operation,
    });

    const { data: inventory, error: fetchError } = await supabase
      .from("inventory")
      .select("committed_quantity")
      .eq("id", inventoryId)
      .single();

    if (fetchError) {
      console.error(
        "Error fetching inventory for committed quantity update:",
        fetchError
      );
      throw fetchError;
    }

    const currentCommitted = inventory.committed_quantity || 0;
    const newCommitted =
      operation === "add"
        ? currentCommitted + quantity
        : Math.max(0, currentCommitted - quantity);

    console.log("Updating committed quantity:", {
      currentCommitted,
      newCommitted,
      operation,
    });

    const { error: updateError } = await supabase
      .from("inventory")
      .update({ committed_quantity: newCommitted })
      .eq("id", inventoryId);

    if (updateError) {
      console.error("Error updating inventory committed quantity:", updateError);
      throw updateError;
    }

    console.log("Successfully updated inventory committed quantity");
  }

  /**
   * Get low stock alerts (items with low available quantity)
   */
  static async getLowStockAlerts(
    threshold: number = 10
  ): Promise<IStockAvailability[]> {
    const { data, error } = (await supabase
      .from("inventory")
      .select(
        `
        id,
        product_id,
        quantity,
        committed_quantity,
        products!inventory_product_id_fkey (
          name,
          sku
        )
      `
      )
      .lt("quantity", threshold)) as {
      data: InventoryRecord[] | null;
      error: SupabaseError | null;
    };

    if (error) {
      throw error;
    }

    if (!data) {
      return [];
    }

    return data
      .map((item) => {
        const available_quantity =
          item.quantity - (item.committed_quantity || 0);
        return {
          inventory_id: item.id,
          product_id: item.product_id,
          total_quantity: item.quantity,
          available_quantity: Math.max(0, available_quantity),
          committed_quantity: item.committed_quantity || 0,
          product_name: item.products.name,
          product_sku: item.products.sku,
        };
      })
      .filter((item) => item.available_quantity <= threshold);
  }

  /**
   * Process backorders when new inventory arrives
   */
  static async processBackordersForProduct(productId: string): Promise<void> {
    console.log("🔄 Processing backorders for product:", productId);

    try {
      // Get all pending backorders for this product
      const { BackorderQueueClient } = await import(
        "@Clients/BackorderQueueClient"
      );
      const pendingBackorders =
        await BackorderQueueClient.getBackorderQueueByProductId(productId);

      console.log("📋 Found pending backorders:", pendingBackorders.length);

      if (pendingBackorders.length === 0) {
        console.log("✅ No pending backorders for product:", productId);
        return;
      }

      // Get all inventory records for this product
      const { data: inventoryRecords, error } = await supabase
        .from("inventory")
        .select("id, quantity, committed_quantity")
        .eq("product_id", productId)
        .order("quantity", { ascending: false }); // Start with records that have the most stock

      if (error) {
        console.error("Error getting inventory records:", error);
        throw error;
      }

      if (!inventoryRecords || inventoryRecords.length === 0) {
        console.log("No inventory records found for product:", productId);
        return;
      }

      // Calculate total available inventory
      const totalAvailable = inventoryRecords.reduce((sum, record) => {
        return sum + (record.quantity - (record.committed_quantity || 0));
      }, 0);

      console.log("📦 Inventory records found:", inventoryRecords.length);
      console.log("📊 Total available inventory:", totalAvailable);

      if (totalAvailable <= 0) {
        console.log("❌ No available inventory for product:", productId);
        return;
      }

      // Process backorders in priority order
      let remainingInventory = totalAvailable;

      for (const backorder of pendingBackorders) {
        if (remainingInventory <= 0) break;

        const fulfillQuantity = Math.min(
          remainingInventory,
          backorder.quantity_needed
        );

        if (fulfillQuantity > 0) {
          // Find the best inventory record to fulfill from
          let quantityToFulfill = fulfillQuantity;

          for (const record of inventoryRecords) {
            const availableInRecord =
              record.quantity - (record.committed_quantity || 0);
            if (availableInRecord > 0) {
              const fulfillFromRecord = Math.min(
                quantityToFulfill,
                availableInRecord
              );

              // Create stock commitment with this inventory record
              await this.createStockCommitment({
                order_id: backorder.order_id,
                order_item_id: backorder.order_item_id,
                inventory_id: record.id,
                product_id: productId,
                quantity_committed: fulfillFromRecord,
                organization_id: backorder.organization_id,
              });

              // Update inventory committed_quantity
              await this.updateInventoryCommittedQuantity(
                record.id,
                fulfillFromRecord,
                "add"
              );

              quantityToFulfill -= fulfillFromRecord;
              remainingInventory -= fulfillFromRecord;

              if (quantityToFulfill <= 0) break;
            }
          }

          const remainingNeeded = backorder.quantity_needed - fulfillQuantity;
          const newStatus = remainingNeeded <= 0 ? "fulfilled" : "pending";
          await BackorderQueueClient.updateBackorderQueue({
            id: backorder.id,
            status: newStatus,
            quantity_needed: Math.max(0, remainingNeeded),
          });

          console.log(
            `✅ Fulfilled ${fulfillQuantity} units for backorder ${backorder.id}, status: ${newStatus}`
          );
        }
      }
    } catch (error) {
      console.error("Error processing backorders:", error);
      throw error;
    }
  }
}
